import React, { useState, useEffect } from 'react';
import { DollarSign, ShoppingCart, MessageSquare, Car, TrendingUp, Clock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/admin/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/admin/orders?limit=5`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setRecentOrders(ordersRes.data.orders);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-[#00F0FF]">Loading...</div>
      </div>
    );
  }

  const statCards = [
    { label: 'Total Revenue', value: formatPrice(stats?.total_revenue || 0), icon: DollarSign, color: '#34C759' },
    { label: 'Total Orders', value: stats?.total_orders || 0, icon: ShoppingCart, color: '#00F0FF' },
    { label: 'Paid Orders', value: stats?.paid_orders || 0, icon: TrendingUp, color: '#34C759' },
    { label: 'Pending Orders', value: stats?.pending_orders || 0, icon: Clock, color: '#FFCC00' },
    { label: 'New Messages', value: stats?.new_contacts || 0, icon: MessageSquare, color: '#FF3B30' },
    { label: 'Custom Requests', value: stats?.new_custom_requests || 0, icon: Car, color: '#AF52DE' },
  ];

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-medium text-[#F8F8F8]">Dashboard</h1>
        <p className="text-[#A0A0A0] mt-1">Welcome back! Here's an overview of your store.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-[#111111] border border-[#222222] p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-10 h-10 bg-[#1A1A1A] border border-[#222222] flex items-center justify-center">
                <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
              </div>
            </div>
            <p className="text-2xl font-bold text-[#F8F8F8]">{stat.value}</p>
            <p className="text-[#A0A0A0] text-sm mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Orders */}
      <div className="bg-[#111111] border border-[#222222] p-6">
        <h2 className="text-lg font-medium text-[#F8F8F8] mb-4">Recent Orders</h2>
        
        {recentOrders.length === 0 ? (
          <p className="text-[#A0A0A0] text-center py-8">No orders yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#222222]">
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-2">Order ID</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-2">Customer</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-2">Items</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-2">Total</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-2">Status</th>
                  <th className="text-left text-[#A0A0A0] text-sm font-medium py-3 px-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order) => (
                  <tr key={order.id} className="border-b border-[#222222] hover:bg-[#1A1A1A]">
                    <td className="py-3 px-2">
                      <span className="text-[#00F0FF] font-mono text-sm">{order.id.slice(0, 8)}...</span>
                    </td>
                    <td className="py-3 px-2">
                      <p className="text-[#F8F8F8] text-sm">{order.customer?.first_name} {order.customer?.last_name}</p>
                      <p className="text-[#666666] text-xs">{order.customer?.email}</p>
                    </td>
                    <td className="py-3 px-2 text-[#F8F8F8] text-sm">{order.items?.length || 1}</td>
                    <td className="py-3 px-2 text-[#F8F8F8] font-medium">{formatPrice(order.total)}</td>
                    <td className="py-3 px-2">
                      <span className={`px-2 py-1 text-xs font-medium ${
                        order.status === 'paid' ? 'bg-[#34C759]/20 text-[#34C759]' :
                        order.status === 'pending' ? 'bg-[#FFCC00]/20 text-[#FFCC00]' :
                        'bg-[#666666]/20 text-[#666666]'
                      }`}>
                        {order.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-[#A0A0A0] text-sm">{formatDate(order.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
